import { apiClient } from './apiClient'
import { environment } from '../../config/environment'
import type { Ingredient, IngredientDetails } from '../../types/ingredient'
import type { PaginatedResponse } from '../../types/common'
import { logger } from '../../utils/logger'

export interface IngredientsFilters {
  page?: number
  limit?: number
  sortBy?: string
  toValidate?: boolean | null
  search?: string
}

export class IngredientsService {
  /**
   * Search ingredients for autocomplete functionality
   */
  static async searchIngredients(
    query: string,
    options: {
      limit?: number
      page?: number
      toValidate?: boolean
    } = {}
  ): Promise<Ingredient[]> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await IngredientsService.getMockSearchResults(query, options)
    }
    
    if (query.length < 2) {
      return []
    }
    
    const params = new URLSearchParams()
    params.append('search', query)
    params.append('limit', (options.limit || 50).toString())
    if (options.page) params.append('page', options.page.toString())
    if (options.toValidate !== null && options.toValidate !== undefined) {
      params.append('toValidate', options.toValidate.toString())
    }
    
    const url = `/v1/ingredients?${params.toString()}`
    logger.log('🔍 Searching ingredients:', url)
    
    const response = await apiClient.get<any>(url)
    logger.log('✅ Ingredient search results:', response)
    
    return response.ingredients || []
  }

  /**
   * Get paginated list of ingredients
   */
  static async getIngredients(filters: IngredientsFilters = {}): Promise<PaginatedResponse<Ingredient>> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await IngredientsService.getMockIngredients(filters)
    }
    
    const params = new URLSearchParams()
    
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.sortBy) params.append('sort', filters.sortBy)
    if (filters.toValidate !== null && filters.toValidate !== undefined) {
      params.append('toValidate', filters.toValidate.toString())
    }
    if (filters.search) params.append('search', filters.search)
    
    const queryString = params.toString()
    const url = `/v1/ingredients${queryString ? `?${queryString}` : ''}`
    
    logger.log('📦 Fetching ingredients from API...', url)
    const response = await apiClient.get<any>(url)
    logger.log('✅ Ingredients received:', response)
    
    return {
      data: response.ingredients || [],
      currentPage: response.currentPage,
      lastPage: response.lastPage,
      firstPage: response.firstPage,
      pageSize: response.pageSize,
      totalRecords: response.totalRecords,
    }
  }

  /**
   * Get a single ingredient by ID
   */
  static async getIngredient(uuid: string): Promise<Ingredient> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await IngredientsService.getMockIngredient(uuid)
    }
    
    const ingredient = await apiClient.get<Ingredient>(`/v1/ingredients/${uuid}`)
    return ingredient
  }

  /**
   * Get ingredient details by ID
   */
  static async getIngredientDetails(uuid: string): Promise<IngredientDetails> {
    const mode = environment.getConfig().mode
    
    if (mode === 'fake') {
      return await IngredientsService.getMockIngredientDetails(uuid)
    }
    
    const rawDetails = await apiClient.get<any>(`/v1/ingredients/${uuid}`)
    
    // Convert rating numbers from backend to Rating enum
    const convertRatings = (ratings: any[]): any[] => {
      if (!ratings || !Array.isArray(ratings)) return []
      return ratings.map(r => ({
        jsonKey: r.jsonKey,
        rating: r.rating === 1 ? 'a' : r.rating === 2 ? 'b' : r.rating === 3 ? 'c' : null
      }))
    }
    
    return {
      ...rawDetails,
      skinTypes: convertRatings(rawDetails.skinTypes),
      skinConditions: convertRatings(rawDetails.skinConditions),
      specificities: convertRatings(rawDetails.specificities),
      allergies: convertRatings(rawDetails.allergies),
      hairTypes: convertRatings(rawDetails.hairTypes),
      hairConditions: convertRatings(rawDetails.hairConditions),
      eyes: convertRatings(rawDetails.eyes),
      lipTypes: convertRatings(rawDetails.lipTypes),
      nails: convertRatings(rawDetails.nails),
    }
  }

  /**
   * Create a new ingredient
   */
  static async createIngredient(ingredient: Partial<IngredientDetails>): Promise<Ingredient> {
    // Convert Rating enum to numbers for backend
    const convertRatingsToNumbers = (ratings: any[]): any[] => {
      if (!ratings || !Array.isArray(ratings)) return []
      return ratings.map(r => ({
        jsonKey: r.jsonKey,
        rating: r.rating === 'a' ? 1 : r.rating === 'b' ? 2 : r.rating === 'c' ? 3 : null
      }))
    }
    
    const payload = {
      ...ingredient,
      skinTypes: convertRatingsToNumbers(ingredient.skinTypes || []),
      skinConditions: convertRatingsToNumbers(ingredient.skinConditions || []),
      specificities: convertRatingsToNumbers(ingredient.specificities || []),
      allergies: convertRatingsToNumbers(ingredient.allergies || []),
      hairTypes: convertRatingsToNumbers(ingredient.hairTypes || []),
      hairConditions: convertRatingsToNumbers(ingredient.hairConditions || []),
      eyes: convertRatingsToNumbers(ingredient.eyes || []),
      lipTypes: convertRatingsToNumbers(ingredient.lipTypes || []),
      nails: convertRatingsToNumbers(ingredient.nails || []),
    }
    
    const createdIngredient = await apiClient.post<Ingredient>('/v1/ingredients', payload)
    return createdIngredient
  }

  /**
   * Update an existing ingredient
   */
  static async updateIngredient(ingredient: Partial<IngredientDetails> & { uuid: string }): Promise<Ingredient> {
    if (!ingredient.uuid) {
      throw new Error('Ingredient UUID is required for update')
    }
    
    // Convert Rating enum to numbers for backend
    const convertRatingsToNumbers = (ratings: any[]): any[] => {
      if (!ratings || !Array.isArray(ratings)) return []
      return ratings.map(r => ({
        jsonKey: r.jsonKey,
        rating: r.rating === 'a' ? 1 : r.rating === 'b' ? 2 : r.rating === 'c' ? 3 : null
      }))
    }
    
    const payload = {
      ...ingredient,
      skinTypes: convertRatingsToNumbers(ingredient.skinTypes || []),
      skinConditions: convertRatingsToNumbers(ingredient.skinConditions || []),
      specificities: convertRatingsToNumbers(ingredient.specificities || []),
      allergies: convertRatingsToNumbers(ingredient.allergies || []),
      hairTypes: convertRatingsToNumbers(ingredient.hairTypes || []),
      hairConditions: convertRatingsToNumbers(ingredient.hairConditions || []),
      eyes: convertRatingsToNumbers(ingredient.eyes || []),
      lipTypes: convertRatingsToNumbers(ingredient.lipTypes || []),
      nails: convertRatingsToNumbers(ingredient.nails || []),
    }
    
    const updatedIngredient = await apiClient.put<Ingredient>(`/v1/ingredients/${ingredient.uuid}`, payload)
    return updatedIngredient
  }

  /**
   * Delete an ingredient
   */
  static async deleteIngredient(uuid: string): Promise<void> {
    await apiClient.delete(`/v1/ingredients/${uuid}`)
  }

  /**
   * Validate an ingredient (set toValidate to false)
   */
  static async validateIngredient(uuid: string): Promise<Ingredient> {
    const validatedIngredient = await apiClient.put<Ingredient>(`/v1/ingredients/${uuid}/validate`, {
      toValidate: false
    })
    return validatedIngredient
  }

  /**
   * Set ingredient validation status (can set toValidate to true or false)
   */
  static async setIngredientValidationStatus(uuid: string, toValidate: boolean): Promise<Ingredient> {
    const updatedIngredient = await apiClient.put<Ingredient>(`/v1/ingredients/${uuid}/validate`, {
      toValidate
    })
    return updatedIngredient
  }

  /**
   * Merge two ingredients
   */
  static async mergeIngredient(ingredientId: string, mergeWithId: string): Promise<Ingredient> {
    const mergedIngredient = await apiClient.put<Ingredient>(
      `/v1/ingredients/${ingredientId}/merge?mergeWith=${mergeWithId}`
    )
    return mergedIngredient
  }

  /**
   * Mock data for development/testing
   */
  private static async getMockSearchResults(
    query: string, 
    options: { limit?: number; page?: number; toValidate?: boolean }
  ): Promise<Ingredient[]> {
    const mockIngredients: Ingredient[] = [
      {
        uuid: 'ing-1',
        inciName: 'AQUA',
        commonName: 'Water',
        hasEssentialOils: false,
        hasAllergens: false,
        hasEcoToxic: false,
        hasEndocrineDisruptors: false,
        toValidate: false,
      },
      {
        uuid: 'ing-2',
        inciName: 'GLYCERIN',
        commonName: 'Glycerol',
        hasEssentialOils: false,
        hasAllergens: false,
        hasEcoToxic: false,
        hasEndocrineDisruptors: false,
        toValidate: false,
      },
      {
        uuid: 'ing-3',
        inciName: 'CETYL ALCOHOL',
        commonName: 'Cetyl Alcohol',
        hasEssentialOils: false,
        hasAllergens: false,
        hasEcoToxic: false,
        hasEndocrineDisruptors: false,
        toValidate: false,
      },
      {
        uuid: 'ing-4',
        inciName: 'SODIUM LAURYL SULFATE',
        commonName: 'SLS',
        hasEssentialOils: false,
        hasAllergens: true,
        hasEcoToxic: true,
        hasEndocrineDisruptors: false,
        toValidate: false,
      },
      {
        uuid: 'ing-5',
        inciName: 'PARFUM',
        commonName: 'Fragrance',
        hasEssentialOils: true,
        hasAllergens: true,
        hasEcoToxic: false,
        hasEndocrineDisruptors: false,
        toValidate: false,
      },
      {
        uuid: 'ing-6',
        inciName: 'TOCOPHEROL',
        commonName: 'Vitamin E',
        hasEssentialOils: false,
        hasAllergens: false,
        hasEcoToxic: false,
        hasEndocrineDisruptors: false,
        toValidate: false,
      },
      {
        uuid: 'ing-7',
        inciName: 'RETINOL',
        commonName: 'Vitamin A',
        hasEssentialOils: false,
        hasAllergens: false,
        hasEcoToxic: false,
        hasEndocrineDisruptors: false,
        toValidate: true,
      },
    ]
    
    const filteredIngredients = mockIngredients.filter(ingredient =>
      ingredient.inciName.toLowerCase().includes(query.toLowerCase()) ||
      (ingredient.commonName && ingredient.commonName.toLowerCase().includes(query.toLowerCase()))
    )
    
    return new Promise((resolve) => {
      setTimeout(() => resolve(filteredIngredients.slice(0, options.limit || 50)), 300)
    })
  }

  private static async getMockIngredients(filters: IngredientsFilters): Promise<PaginatedResponse<Ingredient>> {
    const allIngredients: Ingredient[] = Array.from({ length: 100 }, (_, i) => ({
      uuid: `ingredient-${i + 1}`,
      inciName: `INGREDIENT ${i + 1}`,
      commonName: `Common Name ${i + 1}`,
      hasEssentialOils: i % 5 === 0,
      hasAllergens: i % 7 === 0,
      hasEcoToxic: i % 11 === 0,
      hasEndocrineDisruptors: i % 13 === 0,
      toValidate: i % 8 === 0,
    }))
    
    let filteredIngredients = [...allIngredients]
    
    if (filters.search) {
      const search = filters.search.toLowerCase()
      filteredIngredients = filteredIngredients.filter(ingredient => 
        ingredient.inciName.toLowerCase().includes(search) ||
        (ingredient.commonName && ingredient.commonName.toLowerCase().includes(search))
      )
    }
    
    if (filters.toValidate !== null && filters.toValidate !== undefined) {
      filteredIngredients = filteredIngredients.filter(ingredient => ingredient.toValidate === filters.toValidate)
    }
    
    const page = filters.page || 1
    const limit = filters.limit || 15
    const start = (page - 1) * limit
    const paginatedIngredients = filteredIngredients.slice(start, start + limit)
    
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: paginatedIngredients,
          currentPage: page,
          firstPage: 1,
          lastPage: Math.ceil(filteredIngredients.length / limit),
          pageSize: limit,
          totalRecords: filteredIngredients.length,
        })
      }, 500)
    })
  }

  private static async getMockIngredient(uuid: string): Promise<Ingredient> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          uuid,
          inciName: `Mock Ingredient ${uuid}`,
          commonName: `Mock Common Name`,
          hasEssentialOils: false,
          hasAllergens: false,
          hasEcoToxic: false,
          hasEndocrineDisruptors: false,
          toValidate: false,
        })
      }, 300)
    })
  }

  private static async getMockIngredientDetails(uuid: string): Promise<IngredientDetails> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          uuid,
          inciName: `Mock Ingredient ${uuid}`,
          commonName: `Mock Common Name`,
          casNumber: '123-45-6',
          ecNumber: '123-456-7',
          hasEssentialOils: false,
          hasAllergens: false,
          hasEcoToxic: false,
          hasEndocrineDisruptors: false,
          toValidate: false,
          skinTypes: [],
          skinConditions: [],
          specificities: [],
          allergies: [],
          hairTypes: [],
          hairConditions: [],
          eyes: [],
          lipTypes: [],
          nails: [],
          comments: 'Mock comments',
        })
      }, 300)
    })
  }
}